import CameraAltIcon from '@mui/icons-material/CameraAlt'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import {
  Alert,
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  IconButton,
  Snackbar,
  TextFieldProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { grey, red } from '@mui/material/colors'
import * as Sentry from '@sentry/react'
import {
  ErrorMessage,
  FormikFormProps,
  FormikValues,
  useField,
  useFormikContext,
} from 'formik'
import React, {
  ChangeEvent,
  InputHTMLAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import {
  checkFileExists,
  getFileFromS3,
  putToS3,
} from '../../../hooks/useS3Storage'
import { SnackBarAlertType } from '../../types/interface'
import {
  DocumentType,
  getFileRestriction,
} from '../../utils/documentRestrictions'

interface FileUploadWrapperProps extends InputHTMLAttributes<HTMLInputElement> {
  city?: string
  disabled?: boolean
  documentType?: DocumentType
  fileName?: string
  folder: string
  label: string
  name: string
  value?: string
}

interface ProgressType {
  isLoading: boolean
  success: boolean
}

const FileUploadWrapper: React.FC<FileUploadWrapperProps & TextFieldProps> = ({
  city,
  disabled,
  documentType = 'document',
  fileName,
  folder,
  label,
  name,
  value,
  ...otherProps
}) => {
  const { t } = useTranslation()

  const [field, meta] = useField(name)

  const fileRestriction = useMemo(
    () => getFileRestriction({ city, documentType }),
    [city, documentType]
  )

  const inputRef = useRef<HTMLInputElement>(null)
  const { setFieldError, setFieldTouched, setFieldValue, setSubmitting } =
    useFormikContext<FormikFormProps & FormikValues>()
  const [progress, setProgress] = useState<ProgressType>({
    isLoading: false,
    success: false,
  })
  const [alertEvent, setAlertEvent] = useState<SnackBarAlertType>({
    message: '',
    open: false,
    type: 'info',
  })

  const fileUrl = useMemo(
    () =>
      value
        ? `${import.meta.env.VITE_TRAINING_GUIDE_CLOUDFRONT_URL}${value}`
        : '',
    [value]
  )

  const [preview, setPreview] = useState<null | string>(fileUrl)
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'))

  useEffect(() => {
    const getDocs = async () => {
      if (value) {
        await getFileFromS3(value)
          .then((data) => {
            setPreview(data?.href || null)
          })
          .catch((err) => {
            Sentry.captureException(err)
            console.log(err)
          })
      } else {
        setPreview(null)
      }
    }

    getDocs()
  }, [value])

  useEffect(() => {
    if (!value && folder) {
      setPreview(null)
    }
  }, [value, folder])

  const previewImage = ({ file }: { file: File }) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      setPreview(reader.result as string)
    }
  }

  const isValid = (file: File): boolean => {
    const MAX_FILE_SIZE = 5242880 // 5MB

    if (file.size > MAX_FILE_SIZE) {
      setFieldError(name, 'File size exceeds 5MB limit')
      return false
    }
    if (fileRestriction.formats.includes(file.type)) {
      return true
    }

    setFieldError(name, fileRestriction.errorMessage)
    return false
  }

  const handleUpload = async (file: File) => {
    try {
      const fileNaming = fileName ? `${fileName}_${label}` : label
      Sentry.addBreadcrumb({
        category: 'upload',
        level: 'info',
        message: `Uploading file: [${file.name}] - folder: [${folder}] - label: [${label}]`,
      })

      setSubmitting(true)
      setProgress({
        ...progress,
        isLoading: true,
      })
      const fileToUpload = file
      const response = await putToS3(fileToUpload, folder, fileNaming)

      await checkFileExists(response)

      setFieldValue(name, response)
      previewImage({ file: fileToUpload })
      setSubmitting(false)
      setProgress({
        isLoading: false,
        success: true,
      })
    } catch (error) {
      Sentry.captureException(error)

      setAlertEvent({
        message: t('documents.alerts.error'),
        open: true,
        type: 'error',
      })

      setSubmitting(false)
      setProgress({
        isLoading: false,
        success: false,
      })
    }
  }

  const handleClose = () => {
    setAlertEvent((prevAlertEvent) => ({ ...prevAlertEvent, open: false }))
  }

  const configTextfield = useMemo(() => {
    const config = {
      ...field,
      ...otherProps,
      error: false,
      fullWidth: true,
      inputProps: {
        accept: fileRestriction.extensions.join(', '),
      },
      type: 'file',
      value: field.value || '',
      variant: 'outlined',
    }

    if (meta && meta.touched && meta.error) {
      return {
        ...config,
        error: true,
        helperText: meta.error,
      } as TextFieldProps
    }

    return config as TextFieldProps
  }, [field, fileRestriction.extensions, meta, otherProps])

  const configInput: InputHTMLAttributes<HTMLInputElement> = useMemo(
    () => ({
      ...field,
      accept: fileRestriction.extensions.join(', '),
      hidden: true,
      type: 'file',
    }),
    [field, fileRestriction.extensions]
  )

  const configGridItem = {
    item: true,
    xs: 12,
  }

  const configCardMedia = useMemo(
    () => ({
      sx: {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${preview})`,
        height: '100%',
        left: 0,
        position: 'absolute',
        right: 0,
        top: 0,
        width: '100%',
      },
    }),
    [preview]
  )

  const configIcon = useMemo(
    () => ({
      sx: {
        ...(preview
          ? {
              color: '#FFF',
            }
          : {
              color: grey[400],
            }),
      },
    }),
    [preview]
  )

  const configTypography = useMemo(
    () => ({
      sx: {
        ...(preview
          ? {
              color: '#FFF',
              zIndex: 1,
            }
          : {
              color: grey[800],
            }),
      },
    }),
    [preview]
  )

  const configCardContent = {
    sx: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      my: 1,
    },
  }

  const getIcon = () => {
    if (isLargeScreen) {
      if (progress.success) {
        return <CheckIcon fontSize="large" {...configIcon} />
      }

      return <FolderOpenIcon fontSize="large" {...configIcon} />
    }

    if (progress.success) {
      return <CheckIcon fontSize="large" {...configIcon} />
    }

    return <CameraAltIcon fontSize="large" {...configIcon} />
  }

  const handleChangeFile = async (e: ChangeEvent<HTMLInputElement>) => {
    if (isValid(e.target.files![0])) {
      handleUpload(e.target.files![0])
    } else {
      setFieldTouched(name, true, false)
    }
  }

  return (
    <>
      <Grid container>
        <Grid {...configGridItem}>
          <Typography
            color="text.primary"
            sx={{ textTransform: 'capitalize' }}
            variant="subtitle1"
          >
            {label || name}
          </Typography>
        </Grid>
        <Grid {...configGridItem}>
          <input
            ref={inputRef}
            {...configInput}
            onChange={handleChangeFile}
            value=""
          />
          <Card
            sx={configTextfield.error ? { borderColor: red[500] } : null}
            variant="outlined"
          >
            <CardActionArea
              disabled={progress.isLoading || disabled}
              onClick={() => inputRef.current?.click()}
            >
              {preview ? (
                <CardMedia component="img" height="140" {...configCardMedia} />
              ) : null}
              <CardContent {...configCardContent}>
                <Avatar
                  sx={{
                    height: 72,
                    mb: 2,
                    width: 72,
                    ...(preview
                      ? {
                          backgroundColor: 'transparent',
                          border: `2px solid ${grey[200]}`,
                        }
                      : {
                          backgroundColor: '#FFF',
                          border: `2px solid ${grey[200]}`,
                        }),
                  }}
                  variant="square"
                >
                  {getIcon()}
                  {progress.isLoading && (
                    <CircularProgress size={48} sx={{ position: 'absolute' }} />
                  )}
                </Avatar>
                <Typography variant="h6" {...configTypography}>
                  {t('images.add_picture')}
                </Typography>
                <Typography variant="body2" {...configTypography}>
                  {t('images.upload_picture')}
                </Typography>
                <Typography variant="caption" {...configTypography}>
                  {t('images.max_size')}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
          <ErrorMessage name={name}>
            {(msg) => (
              <Typography sx={{ color: red[600] }} variant="caption">
                {msg}
              </Typography>
            )}
          </ErrorMessage>
        </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={6000}
        onClose={handleClose}
        open={alertEvent.open}
      >
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          }
          elevation={2}
          onClose={handleClose}
          severity={alertEvent.type}
          sx={{
            width: '100%',
          }}
        >
          {alertEvent.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default FileUploadWrapper
